import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  InputAdornment
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import * as customerServices from "../../services/customer";
import * as salengServices from "../../services/saleng";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";

const { v4: uuidv4 } = require("uuid");

/** ------------------------------
 * Shipping Dialog (ซ้าย = สรุปซัพพลายเออร์ + เลือกรถ/คนขับ แบบ toggle, ขวา = ฟอร์มขนส่ง)
 * ------------------------------*/
function ShippingDialog({ open, onClose, onSubmit, customer }) {
  const [shippingName, setShippingName] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // เลือกคันรถ + คนขับ (สามารถกดย้ำเพื่อเอาออกได้)
  const [selectedCarId, setSelectedCarId] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");

  const [carSelectError, setCarSelectError] = useState(false);

  const cars = Array.isArray(customer?.cars) ? customer.cars : [];
  const drivers = Array.isArray(customer?.drivers) ? customer.drivers : [];

  // รถไม่จำเป็นต้องเลือก (optional)
  const mustPickCar = false;

  useEffect(() => {
    if (!open) return;
    // reset ทุกครั้งที่เปิด dialog
    setShippingName("");
    setPoNumber("");
    setDriverName("");
    setPhoneNumber(customer?.phone || "");
    setNote("");

    setSelectedCarId("");
    setSelectedDriverId("");
    setCarSelectError(false);

    setVehicleNumber("");
  }, [open, customer]);

  // --- Toggle เลือกรถ: กดย้ำที่คันเดิม = เอารถออก + ล้างคนขับ + ล้างทะเบียน
  const toggleCar = (id) => {
    const idStr = String(id);
    if (String(selectedCarId) === idStr) {
      // เอารถออก
      setSelectedCarId("");
      setSelectedDriverId(""); // ถ้าเอารถออก คนขับต้องออกด้วย
      setVehicleNumber("");
    } else {
      // เลือกรถใหม่
      setSelectedCarId(idStr);
      setCarSelectError(false);
      setSelectedDriverId(""); // เปลี่ยนรถ => ล้างคนขับ
      const car = cars.find((c) => String(c.id) === idStr);
      setVehicleNumber(car?.license || "");
    }
  };

  // --- Toggle เลือกคนขับ: กดย้ำที่คนเดิม = เอาคนขับออก + ล้างชื่อ
  const toggleDriver = (id) => {
    const idStr = String(id);
    if (String(selectedDriverId) === idStr) {
      // เอาคนขับออก
      setSelectedDriverId("");
      setDriverName("");
    } else {
      // เลือกคนขับใหม่
      setSelectedDriverId(idStr);
      const drv = drivers.find((d) => String(d.id) === idStr);
      const full = [drv?.name, drv?.surname].filter(Boolean).join(" ").trim();
      setDriverName(full || "");
    }
  };

  const handleSubmit = async () => {
    if (mustPickCar && !selectedCarId) {
      setCarSelectError(true);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        shippingName,
        purchaseOrderNumber: poNumber,
        driverName,
        vehicleNumber,
        phoneNumber,
        note,
        carId: selectedCarId ? Number(selectedCarId) : undefined,
        driverId: selectedDriverId ? Number(selectedDriverId) : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const personTypeLabel =
    customer?.personType === "corporate" ? "นิติบุคคล" : "บุคคลธรรมดา";

  const canSubmit = !submitting;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        สร้างรายการขนส่ง {customer ? `· ${customer.name}` : ""}
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* ซ้าย: สรุปข้อมูลซัพพลายเออร์ + เลือกรถ + เลือกคนขับ (toggle) */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    ซัพพลายเออร์
                  </Typography>
                  <Typography variant="h6" fontWeight={700} noWrap>
                    {customer?.name || "-"}
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={personTypeLabel} />
                    {customer?.customerType && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          customer.customerType === "SELLER"
                            ? "ฝั่งขาย (SELLER)"
                            : customer.customerType === "BOTH"
                              ? "ทั้งซื้อ/ขาย (BOTH)"
                              : "ฝั่งซื้อ (BUYER)"
                        }
                      />
                    )}
                  </Stack>

                  <Divider />

                  <Typography variant="body2" color="text.secondary">
                    เบอร์โทร
                  </Typography>
                  <Typography>{customer?.phone || "-"}</Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    ที่อยู่
                  </Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>
                    {customer?.address || "-"}
                  </Typography>

                  <Divider />

                  {/* เลือกรถ (toggle) */}
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      รถที่ลงทะเบียนไว้ {cars.length ? "(เลือกได้ — ไม่บังคับ)" : "(ไม่มีรถลงทะเบียน)"}
                    </Typography>

                    {cars.length ? (
                      <>
                        {/* ใช้ value เป็น selectedCarId แต่ให้ Radio/Label เรียก toggleCar */}
                        <RadioGroup value={selectedCarId}>
                          <Stack
                            spacing={1}
                            sx={{
                              p: 1,
                              borderRadius: 1.5,
                              bgcolor: "action.hover",
                              maxHeight: 180,
                              overflow: "auto",
                            }}
                          >
                            {cars.map((car) => {
                              const checked = String(selectedCarId) === String(car.id);
                              return (
                                <FormControlLabel
                                  key={car.id}
                                  value={String(car.id)}
                                  control={
                                    <Radio
                                      size="small"
                                      checked={checked}
                                      onChange={() => toggleCar(car.id)}
                                    />
                                  }
                                  onClick={() => toggleCar(car.id)}
                                  label={
                                    <Typography color="text.secondary">
                                      {car?.type ? `${car.type} - ` : ""}
                                      {car?.license || "-"}
                                      {car?.province ? ` (${car.province})` : ""}
                                    </Typography>
                                  }
                                  sx={{
                                    m: 0,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    "&:hover": { bgcolor: "action.selected" },
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        </RadioGroup>
                        {carSelectError && (
                          <FormHelperText error sx={{ mt: 1 }}>
                            กรุณาเลือกรถที่ลงทะเบียน 1 คัน
                          </FormHelperText>
                        )}
                      </>
                    ) : (
                      <Typography color="text.secondary">—</Typography>
                    )}
                  </Stack>

                  {/* เลือกคนขับ (toggle) — disabled ถ้ายังไม่เลือกคันรถ */}
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      คนขับที่ลงทะเบียนไว้
                      {drivers.length ? (selectedCarId ? " (เลือกได้)" : " (เลือกได้เมื่อเลือกคันรถ)") : " (ไม่มีข้อมูล)"}
                    </Typography>

                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: "action.hover",
                        maxHeight: 160,
                        overflow: "auto",
                        opacity: selectedCarId ? 1 : 0.5,
                        pointerEvents: selectedCarId ? "auto" : "none",
                      }}
                    >
                      {drivers.length ? (
                        <RadioGroup value={selectedDriverId}>
                          <Stack spacing={1}>
                            {drivers.map((drv) => {
                              const checked = String(selectedDriverId) === String(drv.id);
                              return (
                                <FormControlLabel
                                  key={drv.id}
                                  value={String(drv.id)}
                                  control={
                                    <Radio
                                      size="small"
                                      checked={checked}
                                      onChange={() => toggleDriver(drv.id)}
                                    />
                                  }
                                  onClick={() => toggleDriver(drv.id)}
                                  label={
                                    <Typography color="text.secondary">
                                      {[drv?.name, drv?.surname].filter(Boolean).join(" ") || "-"}
                                      {drv?.idCard ? ` · ${drv.idCard}` : ""}
                                    </Typography>
                                  }
                                  sx={{
                                    m: 0,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    "&:hover": { bgcolor: "action.selected" },
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        </RadioGroup>
                      ) : (
                        <Typography color="text.secondary">—</Typography>
                      )}
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* ขวา: ฟอร์มขนส่ง */}
          <Grid item xs={12} md={7}>
            <Stack spacing={2} sx={{ mt: { xs: 0, md: 1 } }}>
              <TextField
                label="ชื่อการขนส่ง"
                value={shippingName}
                onChange={(e) => setShippingName(e.target.value)}
                fullWidth
              />
              <TextField
                label="เลขที่ใบสั่งซื้อ (PO)"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                fullWidth
              />
              <TextField
                label="ชื่อคนขับ"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                fullWidth
                helperText={
                  selectedDriverId
                    ? "เติมให้อัตโนมัติจากรายการคนขับ (แก้ไขได้)"
                    : "กรอกเองได้ หรือเลือกจากฝั่งซ้ายก็ได้"
                }
              />
              <TextField
                label="เลขตู้ขนส่ง / ทะเบียนรถ"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                fullWidth
                helperText={
                  selectedCarId
                    ? "เลือกจากฝั่งซ้ายแล้วระบบจะกรอกให้อัตโนมัติ (แก้ไขได้)"
                    : "กรอกเองได้ เนื่องจากยังไม่เลือกคันรถ"
                }
              />
              <TextField
                label="โทรศัพท์"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                fullWidth
              />
              <TextField
                label="หมายเหตุ"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                helperText="*แสดงในใบรายการสินค้าและใบขนส่ง"
              />
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          ยกเลิก
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? "กำลังบันทึก..." : "บันทึก"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** ------------------------------ */

function UserList() {
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openShippingDialog, setOpenShippingDialog] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const res = await customerServices.getCustomers("SELLER");
      const data = res?.data?.data || [];
      setCustomers(data);
      return data;
    } catch (error) {
      console.error("Error fetching customers:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // ค้นหาชื่อหรือทะเบียนรถ
  const includesSafe = (a = "", b = "") =>
    String(a || "").toLowerCase().includes(String(b || "").toLowerCase());

  const matchSearch = (customer) => {
    if (!search) return true;
    const byName = includesSafe(customer.name, search);
    const byCarLicense = (customer.cars || []).some((car) =>
      includesSafe(car?.license, search)
    );
    return byName || byCarLicense;
  };

  const filteredData = customers.filter((item) => matchSearch(item));

  const handleCardClick = (customerId) => {
    const customer = filteredData.find((item) => item.id === customerId);
    if (!customer) return;
    setSelectedCustomer(customer);
    setOpenShippingDialog(true);
  };

  const handleShippingSubmit = async (shipping) => {
    if (!selectedCustomer) return;
    console.log('selectedCustomer', selectedCustomer)
    const payload = {
      transactionId: uuidv4(),
      customerId: selectedCustomer.id,
      customerName: selectedCustomer.name,
      ...(shipping.carId ? { carId: shipping.carId } : {}),
      ...(shipping.driverId ? { driverId: shipping.driverId } : {}),
      status: "waiting",
      shipping: {
        purchaseOrderNumber: shipping.purchaseOrderNumber || null,
        shippingName: shipping.shippingName || null,
        driverName: shipping.driverName || null,
        vehicleNumber: shipping.vehicleNumber || null,
        phoneNumber: shipping.phoneNumber || null,
        note: shipping.note,
      },
    };

    const response = await salengServices.createSaleTransaction(payload);
    setOpenShippingDialog(false);

    navigate(`/sale/checkout?id=${response.data.data.transactionId}&ticket=${response.data.data.billNumber}`);
  };

  const handleCreateClick = () => {
    navigate(`/sale-customers/create`);
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <AppBreadcrumbs />

        {/* 🔍 ค้นหา + ปุ่มสร้าง */}
        <Box
          sx={{
            my: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >

          <TextField
            fullWidth
            label="ค้นหา"
            placeholder="ค้นหาชื่อ หรือ ทะเบียนรถ"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2, mt: 2, width: 500 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            sx={{ whiteSpace: "nowrap" }}
          >
            ซัพพลายเออร์ใหม่
          </Button>
        </Box>

        <Card sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} alignItems="stretch">
              {filteredData.map((item) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={item.id}
                  sx={{ display: "flex" }}
                >
                  <Card
                    variant="outlined"
                    onClick={() => handleCardClick(item.id)}
                    sx={{
                      cursor: "pointer",
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      borderColor: "divider",
                      transition: "transform 120ms ease, box-shadow 120ms ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography fontWeight="bold" noWrap>
                        {item.name}
                      </Typography>
                      <Typography color="text.secondary">
                        {item.personType === "normal"
                          ? "บุคคลธรรมดา"
                          : "นิติบุคคล"}
                      </Typography>

                      {Array.isArray(item.cars) && item.cars.length > 0 && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            borderRadius: 1.5,
                            bgcolor: "action.hover",
                          }}
                        >
                          <Typography fontWeight="bold">
                            รถที่ลงทะเบียนไว้
                          </Typography>
                          <Box
                            sx={{
                              maxHeight: 96,
                              overflow: "auto",
                              scrollbarWidth: "none",
                              msOverflowStyle: "none",
                              "&::-webkit-scrollbar": { display: "none" },
                            }}
                          >
                            {item.cars.map((car, idx) => (
                              <Typography key={idx} color="text.secondary">
                                {car?.type ? `${car.type} - ` : ""}
                                {car?.license || "-"}
                                {car?.province ? ` (${car.province})` : ""}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Card>
      </Box>

      {/* โมดัลข้อมูลการขนส่ง */}
      <ShippingDialog
        open={openShippingDialog}
        onClose={() => setOpenShippingDialog(false)}
        onSubmit={handleShippingSubmit}
        customer={selectedCustomer}
      />
    </>
  );
}

export default UserList;
